import React, { useState, useEffect, useCallback } from 'react';
import { X, CheckCircle, User, ArrowsClockwise, Warning, Table, SquaresFour } from '@phosphor-icons/react';
import dayjs from 'dayjs';
import { useOneButtonStore, COMMON_CONSTANTS } from '../../../../store/oneButtonStore';
import quoteServices, { type LeadSyncPayload } from '../../../../services/oneButtonServices/quoteServices';
import './oneButton-css/IGSLeadSyncModal.css';
import { OBD_FINANCE_PARTNER_SYNC_Z } from './oneButtonOverlayZ';
import opportunityInfoServices from '../../../../services/oneButtonServices/opportunityInfoService';
import {
    invalidateRetrievedOpportunityDetails,
    withFinancePartnerLeadList,
} from '../../../../helpers/lightReachLeadId';
import FinancePartnerCreateLeadProgressModal from './FinancePartnerCreateLeadProgressModal';
import { FinancePartnerLead } from '../../../../@types/oneButtonType';
import DashboardModal from './DashboardModal';
import { isEnfin, isGoodLeap, isIgs, leadTypeMatchesSelectedProduct } from './productsFinanceHubHelpers';

// ---------------------------------------------------------------------------
// LeadCard — single lead card 
// ---------------------------------------------------------------------------
const LeadCard: React.FC<{
    lead: FinancePartnerLead;
    isSelected: boolean;
    onSelect: (lead: FinancePartnerLead) => void;
}> = ({ lead, isSelected, onSelect }) => {
    const addressLine1 = lead.Street || '';
    const addressLine2 = [lead.City, lead.State].filter(Boolean).join(', ');
    const formattedDate = lead.LastModifiedDate
        ? dayjs(lead.LastModifiedDate.split('T')[0]).format('MMM D, YYYY')
        : null;

    const handleClick = () => onSelect(lead);
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect(lead);
        }
    };

    return (
        <div
            className={`obd-lead-sync-form__card ${isSelected ? 'obd-lead-sync-form__card--selected' : ''} ${lead.IsCurrentLead ? 'obd-lead-sync-form__card--current' : ''}`}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={handleKeyDown}
        >
            <div className="obd-lead-sync-form__card-head">
                <h4 className="obd-lead-sync-form__card-name">
                    {lead.FirstName} {lead.LastName}
                </h4>
                {formattedDate && (
                    <span className="obd-lead-sync-form__card-date">{formattedDate}</span>
                )}
            </div>
            <div className="obd-lead-sync-form__card-meta">
                <span className="obd-lead-sync-form__card-pill">{lead.lead_Type}</span>
                <span className="obd-lead-sync-form__card-pill">{lead.financePartnerName}</span>
            </div>
            {(addressLine1 || addressLine2) && (
                <p className="obd-lead-sync-form__card-address">
                    {addressLine1}
                    {addressLine1 && addressLine2 && <br />}
                    {addressLine2}
                </p>
            )}
            <div className="obd-lead-sync-form__card-body">
                <div className="obd-lead-sync-form__card-row">
                    <span className="obd-lead-sync-form__card-key">Lead ID</span>
                    <span className="obd-lead-sync-form__card-val">{lead.FinancePartnerSyncLeadId || '—'}</span>
                </div>
                {lead.SalespersonName && (
                    <div className="obd-lead-sync-form__card-row">
                        <span className="obd-lead-sync-form__card-key">Sales</span>
                        <span className="obd-lead-sync-form__card-val">{lead.SalespersonName}</span>
                    </div>
                )}
                {lead.Status && (
                    <div className="obd-lead-sync-form__card-row">
                        <span className="obd-lead-sync-form__card-key">Match Status</span>
                        <span className="obd-lead-sync-form__card-val">{lead.Status}</span>
                    </div>
                )}
                <div className="obd-lead-sync-form__card-row" title={lead.TitleVerified ? 'Title verified' : 'Title not verified'}>
                    <span className="obd-lead-sync-form__card-key">Title</span>
                    <span className={`obd-lead-sync-form__card-val ${lead.TitleVerified ? 'obd-lead-sync-form__card-val--ok' : 'obd-lead-sync-form__card-val--err'}`}>
                        {lead.TitleVerified ? <CheckCircle size={16} weight="fill" /> : <X size={16} weight="bold" />}
                    </span>
                </div>
            </div>
            <div className="obd-lead-sync-form__card-select">
                {isSelected ? (
                    <CheckCircle size={22} weight="fill" className="obd-lead-sync-form__card-check obd-lead-sync-form__card-check--on" />
                ) : (
                    <div className="obd-lead-sync-form__card-check obd-lead-sync-form__card-check--off" aria-hidden />
                )}
            </div>
        </div>
    );
};

export const IGSLeadSyncModal: React.FC = () => {
    const open = useOneButtonStore((s) => s.igsLeadSyncModalOpen); 
    const setOpen = useOneButtonStore((s) => s.setIgsLeadSyncModalOpen); 
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails); 
    const opportunityID = useOneButtonStore((s) => s.opportunityID); 
    const theme = useOneButtonStore((s) => s.theme); 
    const storeProductType = useOneButtonStore((s) => s.productType); 
    const productName = useOneButtonStore((s) => s.leadSyncFormProductName);
    const setRawStatuses = useOneButtonStore((s) => s.setFinancePartnerRawSyncStatuses);

    const [leads, setLeads] = useState<FinancePartnerLead[]>([]); 
    const [viewType, setViewType] = useState<'card' | 'table'>('table'); 
    const [selectedLeads, setSelectedLeads] = useState<FinancePartnerLead[]>([]); 
    const [accountIDSearch, setAccountIDSearch] = useState(''); 
    const [loading, setLoading] = useState({ fetching: false, syncing: false }); 
    const [message, setMessage] = useState<{ type: 'error' | 'success'; text: string } | null>(null); 
    const [progressConfig, setProgressConfig] = useState<{ open: boolean; action: 'create' | 'sync'; payload?: any[] }>({
        open: false,
        action: 'sync'
    });

    const progressProducts = React.useMemo(() => {
        const productsToSync: any[] = [];
        if (progressConfig.action === 'sync' && progressConfig.payload) {
            progressConfig.payload.forEach(p => {
                productsToSync.push({ productType: p.productType, category: p.product_Category || p.productType });
            });
        } else {
            const allProducts = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
            allProducts.forEach((p: any) => {
                const partner = String(
                    p.Finance_Partner_Display_Name ?? p.FinancePartnerDisplayName ?? p.Finance_Partner ?? '',
                );
                if (isGoodLeap(partner) || isEnfin(partner)) return;
                if (partner && !isIgs(partner)) return;
                const active = p.ActiveFlag !== false && p.activeFlag !== false;
                if (!active) return;
                const type = String(
                    p.Product_Type_Name ?? p.product_Type_Name ?? p.ProductTypeName ?? p.ProductCode ?? p.ProductType ?? '',
                ).trim();
                if (!type) return;
                const category = p.ProductCategory || p.Product_Category || type;
                if (!productsToSync.find(ps => ps.productType === type)) {
                    productsToSync.push({ productType: type, category: category });
                }
            });
        }
        return productsToSync;
    }, [progressConfig.action, progressConfig.payload, opportunityDetails]);

    const isBusy = loading.fetching || loading.syncing;

    const onClose = () => {
        setOpen(false);
    };

    const fetchLeads = async () => {
        if (!opportunityID) return;
        setLoading(prev => ({ ...prev, fetching: true }));
        try {
            const res = await opportunityInfoServices.getFinancePartnerLeads(opportunityID);
            let rawLeads: FinancePartnerLead[] = (Array.isArray(res) ? res : res?.Leads || res?.data) || [];
            if (!Array.isArray(rawLeads)) rawLeads = [];
            
            const filtered = rawLeads.filter(ele => {
                const pName = (ele.financePartnerName || '').toUpperCase();
                if (pName !== 'IGS') return false;
                return leadTypeMatchesSelectedProduct(ele.lead_Type, productName);
            });
            
            setLeads(filtered);

            const currentLeads = filtered.filter(l => l.IsCurrentLead);
            if (currentLeads.length > 0) {
                setSelectedLeads(currentLeads);
            } else if (filtered.length === 1) {
                setSelectedLeads([filtered[0]]);
            }
        } catch (err) {
            console.error("IGS Modal - Failed to fetch leads", err);
        } finally {
            setLoading(prev => ({ ...prev, fetching: false }));
        }
    };

    useEffect(() => {
        if (open) {
            fetchLeads();
            setMessage(null);
            setSelectedLeads([]);
            setAccountIDSearch('');
        }
    }, [open, opportunityID, productName]);

    const refreshOpportunity = useCallback(async () => {
        const state = useOneButtonStore.getState();
        const salesforceId = state.opportunityDetails?.SalesForce_Id || state.opportunityDetails?.Salesforce_Id || opportunityID;
        const numericOppId =
            state.opportunityDetails?.Opportunity_Id ??
            state.opportunityDetails?.opportunity_Id ??
            opportunityID;
        if (!salesforceId) return;

        try {
            invalidateRetrievedOpportunityDetails(numericOppId);
            const retrieved =
                numericOppId != null && String(numericOppId).trim() !== ''
                    ? await opportunityInfoServices
                          .retrieveOpportunityDetails(String(numericOppId))
                          .catch(() => null)
                    : null;
            const detail = await opportunityInfoServices.getOpportunityDetail(String(salesforceId));
            const base = detail ?? state.opportunityDetails;
            const merged = withFinancePartnerLeadList(base, retrieved);
            if (merged) state.setOpportunityDetails(merged);
            else if (detail) state.setOpportunityDetails(detail);
        } catch (err) {
            console.error("Failed to refresh opportunity details:", err);
        }
    }, [opportunityID]);

    const getSelectedProductType = (): string => {
        const products = (opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? []);
        const oppInterest = opportunityDetails?.OpportunityInterest || {};
        const financePartners = opportunityDetails?.financePartners || [];

        const hasProduct = (name: string) => {
            const isIGS = (p: string) => {
                const up = (p || '').toUpperCase();
                return up.includes(COMMON_CONSTANTS.IGSPartnerName.toUpperCase());
            };
            if (financePartners.some((f: any) => f.product === name && isIGS(f.financePartner))) return true;
            if (name === 'Solar' && oppInterest.Solar) return true;
            if (name === 'Roof' && (oppInterest.Roof || oppInterest.RRR)) return true;
            if (name === 'Battery' && oppInterest.Battery) return true;
            if (name === 'RRR' && oppInterest.RRR) return true;
            if (name === 'RoofOnly' && oppInterest.RoofOnly) return true;
            if (name === 'BatteryOnly' && oppInterest.BatteryOnly) return true;

            return products.some((p: any) => {
                const type = (p.ProductCode || p.ProductType || '').toUpperCase();
                const partner = (p.Finance_Partner_Display_Name || p.FinancePartnerDisplayName || '').toUpperCase();
                return type === name.toUpperCase() && (isIGS(partner) || partner === '');
            });
        };

        const hasS = hasProduct(COMMON_CONSTANTS.SolarProductName);
        const hasR = hasProduct(COMMON_CONSTANTS.RoofProductName);
        const hasB = hasProduct(COMMON_CONSTANTS.BatteryProductName);
        const hasRRR = hasProduct(COMMON_CONSTANTS.RRRProductName);

        let code = '';
        if (hasS) code = 'S';
        if (hasR) code = code === 'S' ? 'SR' : 'RO';
        if (hasB) {
            if (code === 'S') code = 'SB';
            else if (code === 'SR') code = 'SBR';
            else if (!code) code = 'BO';
        }
        if (hasRRR) {
            if (code === 'S') code = 'SRRR';
            else if (code === 'SB') code = 'SBRRR';
            else if (code === 'BO') code = 'BRRR';
            else if (!code) code = 'RRR';
        }
        return code || storeProductType || '';
    };

    const reSyncProductType = (leadType: any) => {
        let type: any = null;
        if (leadType === COMMON_CONSTANTS.SolarProductName) type = 'S';
        if (leadType === COMMON_CONSTANTS.BatteryOnlyProductName) type = 'BO';
        if (leadType === COMMON_CONSTANTS.RoofOnlyProductName) type = 'RO';
        const partners = opportunityDetails?.financePartners || [];
        const hasRRR = partners.findIndex((ele: any) => ele.product === COMMON_CONSTANTS.RRRProductName) > -1;
        if (leadType === COMMON_CONSTANTS.RoofOnlyProductName && hasRRR) type = COMMON_CONSTANTS.RRRProductName;
        return type || leadType;
    };

    const parseAddress = (Address: any) => {
        if (!Address) return '';
        const suffixes = ['street', 'st', 'avenue', 'ave', 'road', 'rd', 'trail', 'trl', 'drive', 'dr', 'lane', 'ln', 'court', 'crt', 'ct', 'park', 'prk'];
        const parts = String(Address).trim().split(/\s+/);
        if (parts.length > 1 && suffixes.includes(parts[parts.length - 1].toLowerCase().replace(/[^a-z]/g, ''))) {
            return parts.slice(0, -1).join(' ');
        }
        return parts.join(' ');
    };

    const getProductType = (leadType: any) => {
        let productType: any;
        const partners = opportunityDetails?.financePartners || [];
        switch (leadType) {
            case COMMON_CONSTANTS.SolarProductName:
                productType = COMMON_CONSTANTS.SolarProductName;
                break;
            case COMMON_CONSTANTS.BatteryOnlyProductName:
                productType = COMMON_CONSTANTS.BatteryOnlyProductName;
                break;
            case COMMON_CONSTANTS.RoofOnlyProductName:
                productType = (partners.findIndex((ele: any) => ele.product === COMMON_CONSTANTS.RRRProductName) > -1) 
                    ? COMMON_CONSTANTS.RRRProductName 
                    : COMMON_CONSTANTS.RoofOnlyProductName;
                break;
            default:
                productType = leadType;
        }
        return productType;
    };

    const extractProductDisplay = (product: string) => {
        switch (product) {
            case 'S': return 'Solar Only';
            case 'RO': return 'Roof Only';
            case 'SR': return 'Solar;Roof';
            case 'SB': return 'Solar;Battery';
            case 'SBR': return 'Solar;Roof;Battery';
            case 'RRR': return 'RRR';
            case 'SRRR': return 'Solar;RRR';
            case 'SBRRR': return 'Solar;RRR;Battery';
            default: return product;
        }
    };

    const handleLeadSelect = (lead: FinancePartnerLead) => {
        setSelectedLeads(current => {
            const hasLead = current.some(l => l.FinancePartnerSyncLeadId === lead.FinancePartnerSyncLeadId);
            const others = current.filter(l => l.lead_Type !== lead.lead_Type);
            return hasLead ? current.filter(l => l.FinancePartnerSyncLeadId !== lead.FinancePartnerSyncLeadId) : [...others, lead];
        });
    };

    const handleSyncSelectedLeads = async (isNewLead = false) => {
        if (!opportunityID) return;
        if (isNewLead) {
            setProgressConfig({ open: true, action: 'create' });
            return;
        }
        if (selectedLeads.length === 0) {
            setMessage({ type: 'error', text: 'Please select a lead to continue' });
            return;
        }

        let syncSuccessCount = 0;
        let addressMatchCount = 0;
        let leadTypeMatchCount = 0;
        const selectedProductType = getSelectedProductType();

        selectedLeads.forEach((ele) => {
            const leadType = reSyncProductType(ele.lead_Type);
            const normalizedLeadType = String(leadType);
            if (selectedProductType.includes(normalizedLeadType)) {
                const extractedStreet = parseAddress(ele.Street);
                const oppStreet = parseAddress(opportunityDetails?.InstallationAddress?.Street);
                const cityMatch = (opportunityDetails?.InstallationAddress?.City || '').toLowerCase() === (ele.City || '').toLowerCase();
                const streetMatch = (oppStreet || '').toLowerCase() === extractedStreet.toLowerCase();
                const stateMatch = (opportunityDetails?.InstallationAddress?.stateCode || opportunityDetails?.InstallationAddress?.State || '').toLowerCase() === (ele.State || '').toLowerCase();
                if (cityMatch && streetMatch && stateMatch) syncSuccessCount++;
                else addressMatchCount++;
            } else leadTypeMatchCount++;
        });

        const isComplexProduct = ['SBR', 'SB', 'RRR', 'SBRRR', 'SRRR', 'BRRR'].includes(selectedProductType);
        if (isComplexProduct || (syncSuccessCount > 0 && syncSuccessCount === selectedLeads.length)) {
            await performSync();
        } else if (leadTypeMatchCount > 0 && reSyncProductType(selectedLeads[0].lead_Type) !== 'RO') {
            const appProduct = extractProductDisplay(selectedProductType);
            setMessage({
                type: 'error',
                text: `Selected product type in oneBUTTON (${appProduct || '(None)'}) does not match the lead type of ${selectedLeads[0].lead_Type}. Please update the product type in oneBUTTON or choose a matching lead.`,
            });
            return;
        } else if (addressMatchCount > 0) {
            const proceed = window.confirm('Installation address and lead address do not match. Continue sync?');
            if (proceed) await performSync();
        } else await performSync();
    };

    const performSync = async () => {
        let creditCheckCount = 0;
        for (const ele of selectedLeads) {
            if (Number(ele.DaysSinceCreditRun) === 0) {
                const passCount = leads.filter(l => 
                    l.lead_Type === ele.lead_Type && 
                    l.FinancePartnerSyncLeadId !== ele.FinancePartnerSyncLeadId &&
                    Number(l.DaysSinceCreditRun) > 0 && 
                    Number(l.DaysSinceCreditRun) <= 90
                ).length;
                if (passCount > 0) creditCheckCount++;
            }
        }
        if (creditCheckCount > 0) {
            const proceed = window.confirm('You are choosing lead(s) with no recent credit pass. Continue?');
            if (!proceed) return;
        }
        const syncPayload: LeadSyncPayload[] = selectedLeads.map(ele => {
            const leadType = (ele.lead_Type || '').toLowerCase();
            let shortCode = 'S';
            if (leadType.includes('solar')) shortCode = 'S';
            else if (leadType.includes('roof')) shortCode = 'R';
            else if (leadType.includes('battery')) shortCode = 'B';
            else if (leadType.includes('rrr')) shortCode = 'RRR';
            return {
                finance_Partner_Lead_Sync_Id: ele.FinancePartnerSyncLeadId,
                productType: getProductType(ele.lead_Type),
                address: false,
                title: false,
                product_Category: shortCode
            };
        });
        setProgressConfig({ open: true, action: 'sync', payload: syncPayload });
    };

    return (
        <>
        <DashboardModal
            open={open}
            onClose={onClose}
            footer={null}
            title="Finance Partner Sync — IGS"
            width={"95%"}
            maskClosable={false}
            zIndex={OBD_FINANCE_PARTNER_SYNC_Z}
            theme={theme}
            className={`obd-sync-modal igs-lead-sync-modal obd-theme-${theme}`}
            rootClassName={`obd-sync-modal-root obd-theme-${theme}`}
            destroyOnClose
            loading={isBusy}
            loadingMessage={loading.syncing ? 'Processing...' : 'Loading leads...'}
        >
            <div className="obd-sync-modal__inner" style={{ position: 'relative' }}>
                <div className="obd-lead-sync-form__body">
                    <div className="obd-lead-sync-form__igs-section">
                        <div className="obd-lead-sync-form__leads-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', marginBottom: '12px' }}>
                            {leads.some(l => l.financePartnerName === 'IGS') ? (
                                <div className="obd-lead-sync-form__legend" style={{ padding: 0, margin: 0 }}>
                                    <div className="obd-lead-sync-form__legend-icon" />
                                    <span>Current Lead</span>
                                </div>
                            ) : <div />}

                            <div className="obd-quotes-view-controls">
                                <button
                                    type="button"
                                    className={`obd-quotes-view-btn ${viewType === 'card' ? 'obd-quotes-view-btn--active' : ''}`}
                                    onClick={() => setViewType('card')}
                                    title="Card View"
                                >
                                    <SquaresFour size={16} weight="regular" /><span>Card</span>
                                </button>
                                <button
                                    type="button"
                                    className={`obd-quotes-view-btn ${viewType === 'table' ? 'obd-quotes-view-btn--active' : ''}`}
                                    onClick={() => setViewType('table')}
                                    title="Table View"
                                >
                                    <Table size={16} weight="regular" /><span>Table</span>
                                </button>
                            </div>
                        </div>

                        {leads.length === 0 || viewType === 'table' ? (
                            <div className="obd-lead-sync-form__table-container">
                                <table className="obd-lead-sync-form__table">
                                    <thead>
                                        <tr>
                                            <th className="obd-lead-sync-form__th"><div>Select Lead</div></th>
                                            <th className="obd-lead-sync-form__th"><div>Lead ID</div></th>
                                            <th className="obd-lead-sync-form__th"><div>Lead Type</div></th>
                                            <th className="obd-lead-sync-form__th"><div>Finance Partner</div></th>
                                            <th className="obd-lead-sync-form__th"><div>First Name</div></th>
                                            <th className="obd-lead-sync-form__th"><div>Last Name</div></th>
                                            <th className="obd-lead-sync-form__th"><div>Address</div></th>
                                            <th className="obd-lead-sync-form__th"><div>City</div></th>
                                            <th className="obd-lead-sync-form__th"><div>State</div></th>
                                            <th className="obd-lead-sync-form__th"><div>Sales Person</div></th>
                                            <th className="obd-lead-sync-form__th"><div>Title Verified</div></th>
                                            <th className="obd-lead-sync-form__th"><div>Credit Status</div></th>
                                            <th className="obd-lead-sync-form__th"><div>Days Since Credit Run</div></th>
                                            <th className="obd-lead-sync-form__th"><div>Last Modified</div></th>
                                            <th className="obd-lead-sync-form__th"><div>Match Status</div></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {leads.length === 0 ? (
                                            <tr>
                                                <td colSpan={15} className="obd-lead-sync-form__empty">
                                                    <div>Lead not found.</div>
                                                </td>
                                            </tr>
                                        ) : (
                                            leads.map((lead) => (
                                                <tr key={lead.FinancePartnerSyncLeadId} className={lead.IsCurrentLead ? 'active-lead' : ''}>
                                                    <td className="obd-lead-sync-form__td">
                                                        <input 
                                                            type="checkbox" 
                                                            className="obd-lead-sync-form__checkbox"
                                                            checked={selectedLeads.some(l => l.FinancePartnerSyncLeadId === lead.FinancePartnerSyncLeadId)}
                                                            onChange={() => handleLeadSelect(lead)}
                                                        />
                                                    </td>
                                                    <td className="obd-lead-sync-form__td">{lead.FinancePartnerSyncLeadId}</td>
                                                    <td className="obd-lead-sync-form__td">{lead.lead_Type}</td>
                                                    <td className="obd-lead-sync-form__td">{lead.financePartnerName}</td>
                                                    <td className="obd-lead-sync-form__td">{lead.FirstName}</td>
                                                    <td className="obd-lead-sync-form__td">{lead.LastName}</td>
                                                    <td className="obd-lead-sync-form__td">{lead.Street}</td>
                                                    <td className="obd-lead-sync-form__td">{lead.City}</td>
                                                    <td className="obd-lead-sync-form__td">{lead.State}</td>
                                                    <td className="obd-lead-sync-form__td">{lead.SalespersonName}</td>
                                                    <td className="obd-lead-sync-form__td">
                                                        <div className="obd-lead-sync-form__status-icon">
                                                            {lead.TitleVerified ? (
                                                                <CheckCircle size={18} weight="fill" className="obd-lead-sync-form__status-icon--success" />
                                                            ) : (
                                                                <X size={18} weight="bold" className="obd-lead-sync-form__status-icon--error" />
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="obd-lead-sync-form__td">{lead.CreditStatus}</td>
                                                    <td className="obd-lead-sync-form__td">{lead.DaysSinceCreditRun}</td>
                                                    <td className="obd-lead-sync-form__td">
                                                        {lead.LastModifiedDate ? dayjs(lead.LastModifiedDate.split('T')[0]).format('MM-DD-YYYY') : '-'}
                                                    </td>
                                                    <td className="obd-lead-sync-form__td">{lead.Status}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="obd-lead-sync-form__leads-grid" style={{ padding: '0 20px 20px' }}>
                                {leads.map((lead) => (
                                    <LeadCard
                                        key={lead.FinancePartnerSyncLeadId}
                                        lead={lead}
                                        isSelected={selectedLeads.some(
                                            (l) => l.FinancePartnerSyncLeadId === lead.FinancePartnerSyncLeadId
                                        )}
                                        onSelect={handleLeadSelect}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="obd-sync-modal__footer">
                    {message && (
                        <div
                            className={
                                message.type === 'error'
                                    ? 'obd-lead-sync-form__message obd-lead-sync-form__message--error'
                                    : 'obd-lead-sync-form__message obd-lead-sync-form__message--success'
                            }
                            style={{ marginRight: 'auto', marginBottom: 0 }}
                        >
                            {message.type === 'error' && <Warning size={16} weight="fill" style={{ marginRight: 6 }} />}
                            {message.text}
                        </div>
                    )}
                    <div className="obd-sync-modal__footer-btns">
                        {leads.length > 0 && (
                            <button
                                type="button"
                                className="obd-sync-modal__btn obd-sync-modal__btn--primary obd-lead-sync-form__sync-btn"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                                onClick={() => handleSyncSelectedLeads(false)}
                                disabled={isBusy || selectedLeads.length === 0}
                            >
                                <ArrowsClockwise size={18} weight="bold" className={loading.syncing ? 'animate-spin' : ''} />
                                {loading.syncing ? 'Syncing…' : 'Sync'}
                            </button>
                        )}

                        <button
                            type="button"
                            className="obd-sync-modal__btn obd-sync-modal__btn--primary obd-lead-sync-form__create-new-btn"
                            onClick={() => handleSyncSelectedLeads(true)}
                            disabled={isBusy || !opportunityID}
                            aria-label="Create a new lead"
                        >
                            <User size={18} weight="duotone" aria-hidden />
                            Create a new lead
                        </button>

                        <button type="button" className="obd-sync-modal__btn obd-sync-modal__btn--secondary" onClick={onClose}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </DashboardModal>

        {progressConfig.open && (
            <FinancePartnerCreateLeadProgressModal
                open={progressConfig.open}
                onClose={async () => {
                    setProgressConfig(prev => ({ ...prev, open: false }));
                    setLoading(prev => ({ ...prev, fetching: true }));
                    try {
                        await fetchLeads(); 
                        const stats = await quoteServices.getSyncStatusList(String(opportunityID));
                        const list = stats?.FinancePartnerSyncStatuses || stats || [];
                        if (Array.isArray(list)) setRawStatuses(list);
                        
                        // Also update opportunity details in the store
                        await refreshOpportunity();
                    } finally {
                        setLoading(prev => ({ ...prev, fetching: false }));
                    }
                }}
                opportunityId={String(opportunityID)}
                partnerName="IGS"
                action={progressConfig.action}
                syncPayload={progressConfig.payload}
                products={progressProducts}
            />
        )}
        </>
    );
};

export default IGSLeadSyncModal;
